// Asset-transaction cfg lookups delivered via get_global_configs_v3:
// cfg.asset_transaction_types (flattened with their pattern's behaviour
// flags), cfg.asset_transaction_scopes, cfg.asset_valuation_types. These
// drive the booking form's dynamic field visibility - no per-type logic is
// hardcoded in components.
import { z } from 'zod'

export const AssetTransactionTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    pattern_id: z.number(),
    pattern_label: z.string(),
    tranche_created: z.boolean(),
    tranche_targeted: z.boolean(),
    has_scope: z.boolean(),
    countable_only: z.boolean(),
    targets_existing: z.boolean(),
    uses_price: z.boolean(),
    uses_split_factor: z.boolean(),
    uses_units_disposed: z.boolean(),
    // basis-allocation patterns (Distribution - Securities): the booking
    // captures % of cost allocated to the received tranche
    uses_cost_allocation: z.boolean().optional().default(false),
    // interest (PR B): a conversion capitalises the targeted tranche's accrued
    // interest (the form shows the override); an accrual carries its period end
    uses_interest_capitalization: z.boolean().optional().default(false),
    uses_interest_period_end: z.boolean().optional().default(false),
    is_secondary: z.boolean(),
    amount_label: z.string().nullish(),
    has_additional_payment: z.boolean(),
    exercisable_only: z.boolean(),
    escrow_column: z.string().nullish(),
    journals_separate_tab: z.boolean()
  })
  .strict()

export const AssetTransactionScopeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    target_field: z.string().nullish()
  })
  .strict()

export const AssetValuationTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    is_countable: z.boolean(),
    is_exercisable: z.boolean(),
    suffix: z.string().nullish()
  })
  .strict()

export const SecurityTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    default_valuation_type_id: z.number().nullish(),
    // Security-form field visibility flags (cfg.security_types). Optional
    // with defaults so a frontend deployed ahead of the backend still parses
    // (fund_groups precedent).
    is_convertible: z.boolean().optional().default(false),
    uses_interest: z.boolean().optional().default(false),
    uses_nominal: z.boolean().optional().default(false),
    uses_strike: z.boolean().optional().default(false),
    uses_issue_price: z.boolean().optional().default(false),
    uses_maturity: z.boolean().optional().default(false),
    // FD-common-equivalent default applied to new securities of this type
    // (1 for equity/warrant types; null = excluded from FD-eq holdings).
    default_fd_common_equivalent: z.number().nullish()
  })
  .strict()

// cfg.valuation_cap_types — SAFE/convertible cap terms. Delivered in id
// order, which IS the display order (hand-assigned ids).
export const ValuationCapTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    has_cap: z.boolean().optional().default(false),
    has_floor: z.boolean().optional().default(false)
  })
  .strict()

// cfg.interest_rate_types — Simple/Compound; `compounds` gates the
// compounding-frequency select on the Security form.
export const InterestRateTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    compounds: z.boolean().optional().default(false)
  })
  .strict()

// cfg.interest_payment_types — Accrued (PIK) / Cash-pay: how an
// interest-bearing security pays its interest (the Security form's Interest
// terms section; docs/INTEREST_ACCRUAL_DESIGN.md section 3.3). Informational:
// it drives defaults and the workbench's expectation, never the formulas.
export const InterestPaymentTypeModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    description: z.string().nullish()
  })
  .strict()

// cfg.at_accounting_treatments — accounting treatments per transaction
// pattern (the booking form's treatment selector; the chosen treatment
// decides which journal/delta rules fire and which value input shows).
export const AtAccountingTreatmentModelSchema = z
  .object({
    id: z.number(),
    pattern_id: z.number(),
    key: z.string(),
    label: z.string(),
    uses_percent: z.boolean(),
    uses_amount: z.boolean(),
    // NULL = every type of the pattern; else the one type the treatment is
    // bound to (the Conversion's Carryover basis / Fair value pair, 2026-10-21)
    asset_transaction_type_id: z.number().nullish(),
    // the treatment books off the fair value received (the form shows the
    // "Fair value received" pair and the gain hint)
    uses_fair_value: z.boolean().optional().default(false),
    sequence: z.number().nullish()
  })
  .strict()

export type AssetTransactionTypeModel = z.infer<typeof AssetTransactionTypeModelSchema>
export type AtAccountingTreatmentModel = z.infer<typeof AtAccountingTreatmentModelSchema>
export type AssetTransactionScopeModel = z.infer<typeof AssetTransactionScopeModelSchema>
export type AssetValuationTypeModel = z.infer<typeof AssetValuationTypeModelSchema>
export type SecurityTypeModel = z.infer<typeof SecurityTypeModelSchema>
export type ValuationCapTypeModel = z.infer<typeof ValuationCapTypeModelSchema>
export type InterestRateTypeModel = z.infer<typeof InterestRateTypeModelSchema>
export type InterestPaymentTypeModel = z.infer<typeof InterestPaymentTypeModelSchema>
